import { Button } from "@clockwise/design-system";
import { ProposalAppliesTo, TradeoffType } from "@clockwise/schema/v2";
import { useGatewayMutation } from "@clockwise/web-commons/src/network/apollo/gateway-provider";
import { EventThread, SecondaryCard } from "@clockwise/web-commons/src/ui/event-thread";
import { getFormattedDateRange } from "@clockwise/web-commons/src/util/date.util";
import { Dialog } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { DateTime } from "luxon";
import React, { useCallback } from "react";
import toast from "react-hot-toast";
import { SaveDropdownForProposal } from "../event-card/atoms/SaveDropdown";
import { TradeoffBlock } from "../tradeoffs";
import { RemoveTradeoffFromProposalDocument } from "../tradeoffs/__generated__/RemoveTradeoffFromProposal.v2.generated";
import { getTradeoffBlocksToDisplay } from "../tradeoffs/tradeoffs.utils";
import { FindMoreTimesButton } from "../web-app-calendar/event-context-menu/atoms/QuickReschedule";
import { toRenderableTradeoffs } from "./toRenderableTradeoffs";

type AffectedAttendee = {
  person: {
    isMe: boolean;
    displayName: string;
    externalImageUrl: string | null;
    email: string;
  };
};

type TradeoffAttendee = {
  person: {
    givenName: string | null;
    familyName: string | null;
    externalImageUrl: string | null;
  };
};

type GQLTime =
  | {
      __typename: "DateRange";
      dateRange: string;
    }
  | {
      __typename: "DateTimeRange";
      dateTimeRange: string;
    }
  | null;

export type Tradeoff = {
  title: string;
  attendees: TradeoffAttendee[];
  event: {
    title: string;
  } | null;
  updatedTime?: GQLTime;
};

export type TradeoffBlock = {
  tradeoffs: Tradeoff[];
  affectedAttendees: AffectedAttendee[];
  tradeoffType: TradeoffType;
};

type OnConfirmProposalForRecurringEvent = (appliesTo: ProposalAppliesTo) => void;
type OnSave = (externalEventId: string, start: string) => void;

type Props = {
  externalEventId: string;
  proposedStart: string;
  existingStart: string;
  title: string;
  duration: number;
  newDuration?: number;
  isOpen: boolean;
  tradeoffBlocks?: TradeoffBlock[];
  disabled?: boolean;
  loading?: boolean;
  onClose: () => void;
  onSave: OnSave;
  submitRescheduleMessage?: () => void;
  onConfirmProposalForRecurringEvent?: OnConfirmProposalForRecurringEvent;
  isReccuring?: boolean;
  isOnDNDProposal?: boolean;
};

const SaveEventButton = ({
  disabled,
  onSave,
  externalEventId,
  proposedStart,
  text = "Save & send",
}: {
  disabled: boolean;
  onSave: OnSave;
  externalEventId: string;
  proposedStart: string;
  text?: string;
}) => {
  return (
    <Button
      sentiment="positive"
      size="xsmall"
      onClick={() => onSave(externalEventId, proposedStart)}
      disabled={disabled}
    >
      {text}
    </Button>
  );
};
const SaveSBMProposalButton = ({
  disabled,
  onSave,
  text = "Save & send",
}: {
  disabled: boolean;
  onSave: () => void;
  text?: string;
}) => {
  return (
    <Button
      sentiment="neutral"
      variant="outlined"
      size="xsmall"
      onClick={() => onSave()}
      disabled={disabled}
    >
      {text}
    </Button>
  );
};

const FIX_WITH_SBM_TEXT = "Save and fix conflicts";
const SaveProposalRecurringAndNonRecurringButtons = ({
  disabled,
  onConfirmProposalForRecurringEvent,
  isReccuring,
  onSave,
  externalEventId,
  proposedStart,
  isSBM,
}: {
  disabled: boolean;
  onConfirmProposalForRecurringEvent?: OnConfirmProposalForRecurringEvent;
  isReccuring: boolean;
  onSave: OnSave;
  externalEventId: string;
  proposedStart: string;
  isSBM: boolean;
}) => {
  return (
    <div className="cw-flex cw-gap-2 cw-flex-row">
      {isReccuring && onConfirmProposalForRecurringEvent && !isSBM && (
        <SaveDropdownForProposal
          disabled={disabled}
          onSelect={onConfirmProposalForRecurringEvent}
          submissionCopy={{
            default: FIX_WITH_SBM_TEXT,
            singleEventOnly: "Apply to this event only",
            thisAndFutureEvents: "Apply to this and future events",
            allEvents: "Apply to all events",
          }}
          variant="default"
          sentiment="neutral"
        />
      )}
      {onConfirmProposalForRecurringEvent && isSBM && (
        <SaveSBMProposalButton
          disabled={disabled}
          onSave={() => {
            onConfirmProposalForRecurringEvent(ProposalAppliesTo.Instance);
          }}
          text={FIX_WITH_SBM_TEXT}
        />
      )}

      <SaveEventButton
        disabled={false}
        onSave={onSave}
        externalEventId={externalEventId}
        proposedStart={proposedStart}
        text="Save"
      />
    </div>
  );
};

export const RescheduleConfirmationModal = ({
  externalEventId,
  title,
  onClose,
  duration,
  newDuration,
  proposedStart,
  existingStart,
  onSave,
  isOpen,
  submitRescheduleMessage,
  tradeoffBlocks = [],
  disabled = false,
  onConfirmProposalForRecurringEvent,
  isReccuring = false,
  isOnDNDProposal = false,
  loading = false,
}: Props) => {
  const isSBM = tradeoffBlocks.some((block) => block.tradeoffType === TradeoffType.FixableConflict);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      PaperProps={{ className: "cw-w-[400px] cw-rounded-xl" }}
      BackdropProps={{
        classes: { root: "cw-bg-[#000000] cw-bg-opacity-30" },
      }}
    >
      <div className="cw-flex-col cw-body-sm">
        <div className="cw-py-2 cw-px-4 cw-border-b cw-border-solid cw-border-muted cw-font-semibold cw-text-default">
          Move event
        </div>
        <div className="cw-flex-col cw-py-3 cw-px-4 cw-min-h-[165px]">
          <EventThread>
            <EventThread.Source>
              <div
                className={
                  "cw-border cw-border-solid cw-border-muted cw-bg-default cw-rounded-xl cw-px-3 cw-py-2"
                }
              >
                <div className="cw-font-bold cw-py-1"> {title}</div>
                <ProposedTime start={proposedStart} duration={newDuration || duration} />
                <OriginalTime start={existingStart} duration={duration} />
              </div>
            </EventThread.Source>
            <TradeoffBlocks blocks={tradeoffBlocks} loading={loading} />
          </EventThread>
        </div>

        {submitRescheduleMessage && (
          <div className="cw-flex cw-flex-wrap cw-gap-1 cw-my-2 cw-px-4">
            <FindMoreTimesButton
              shouldExcludeAi={false}
              submitRescheduleMessage={submitRescheduleMessage}
              disabled={false}
              componentType="Button"
            />
          </div>
        )}

        <div className="cw-flex cw-justify-between cw-border-t cw-border-solid cw-border-muted cw-py-2 cw-px-3">
          <Button variant="outlined" sentiment="neutral" size="xsmall" onClick={onClose}>
            Discard
          </Button>
          {isOnDNDProposal ? (
            <SaveProposalRecurringAndNonRecurringButtons
              disabled={disabled || loading}
              onConfirmProposalForRecurringEvent={onConfirmProposalForRecurringEvent}
              isReccuring={isReccuring}
              onSave={onSave}
              externalEventId={externalEventId}
              proposedStart={proposedStart}
              isSBM={isSBM}
            />
          ) : (
            <SaveEventButton
              disabled={disabled || loading}
              onSave={onSave}
              externalEventId={externalEventId}
              proposedStart={proposedStart}
            />
          )}
        </div>
      </div>
    </Dialog>
  );
};

const ProposedTime = ({ start, duration }: { start: string; duration: number }) => {
  const endTime = DateTime.fromISO(start).plus({ minutes: duration }).toISO();
  const dateString = getFormattedDateRange({ start, end: endTime });
  return (
    <div className="cw-flex cw-body-sm cw-w-full cw-justify-between">
      <div className="cw-w-[240px]"> {dateString} </div>
      <div className="cw-text-12 cw-text-subtle-disabled cw-pl-2"> New </div>
    </div>
  );
};

const OriginalTime = ({ start, duration }: { start: string; duration: number }) => {
  const endTime = DateTime.fromISO(start).plus({ minutes: duration }).toISO();
  const dateString = getFormattedDateRange({ start, end: endTime });
  return (
    <div className="cw-flex cw-body-sm cw-w-full cw-justify-between">
      <div className="cw-w-[240px] cw-text-subtle cw-line-through"> {dateString} </div>
      <div className="cw-text-12 cw-text-subtle-disabled cw-pl-2"> Original</div>
    </div>
  );
};

const getTradeoffRenderType = (tradeoffType: TradeoffType): SecondaryCard => {
  switch (tradeoffType) {
    case TradeoffType.AvailabilityIssue:
      return "unavailable";
    case TradeoffType.Inconvenience:
      return "inconvenience";
    case TradeoffType.LowVisibilityInconvenience:
      return "inconvenience";
    case TradeoffType.FixableConflict:
      return "sbm";
    default:
      return "unavailable";
  }
};

const TradeoffBlocks = ({ blocks, loading }: { blocks: TradeoffBlock[]; loading?: boolean }) => {
  const [removeTradeoffFromProposal] = useGatewayMutation(RemoveTradeoffFromProposalDocument);
  const handleRemovalOfTradeoff = useCallback(
    (diffId: string) => {
      void removeTradeoffFromProposal({
        variables: { eventDiffId: diffId },
        onCompleted: () => {
          toast.success("Tradeoff removed");
        },
        onError: () => {
          toast.error("Failed to remove tradeoff");
        },
      });
    },
    [removeTradeoffFromProposal],
  );

  if (loading) return <TradeoffBlocksSkeleton />;

  return (
    <>
      {getTradeoffBlocksToDisplay(blocks).map(
        ({ tradeoffType, affectedAttendees, tradeoffs }, index) => (
          <TradeoffBlock
            key={`${tradeoffType}-${index}`}
            tradeoffs={toRenderableTradeoffs(tradeoffs)}
            affectedAttendees={affectedAttendees}
            type={getTradeoffRenderType(tradeoffType)}
            defaultCollapsed={tradeoffType !== TradeoffType.FixableConflict}
            onClickX={handleRemovalOfTradeoff}
          />
        ),
      )}
    </>
  );
};

const TradeoffBlocksSkeleton = () => {
  return (
    <div className="cw-mx-3 cw-flex cw-flex-col cw-gap-3 cw-justify-start">
      <div className="cw-flex cw-gap-4">
        <Skeleton variant="circle" width={20} height={20} />
        <div className="cw-flex cw-flex-col">
          <Skeleton variant="text" width={300} height={20} />
          <Skeleton variant="text" width={90} height={20} className="" />
        </div>
      </div>
    </div>
  );
};
